import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  Icon,
  Typography,
} from '@shared/ui';
import React from 'react';
import type { ExecutionStep } from './useAgentStreaming';

interface PhaseAccordionProps {
  steps: ExecutionStep[];
  isStreaming: boolean;
  error?: string | null;
  onStepClick?: (step: ExecutionStep) => void;
}

interface Phase {
  name: string;
  icon: string;
  steps: ExecutionStep[];
  status: 'pending' | 'in_progress' | 'complete' | 'error';
}

function groupStepsIntoPhases(steps: ExecutionStep[]): Phase[] {
  const phases: Phase[] = [
    { name: 'Planning', icon: 'psychology', steps: [], status: 'pending' },
    { name: 'Tool Execution', icon: 'build', steps: [], status: 'pending' },
    { name: 'Response Generation', icon: 'edit_note', steps: [], status: 'pending' },
  ];

  steps.forEach((step) => {
    if (step.type === 'planner_decision') {
      phases[0].steps.push(step);
    } else if (step.type === 'tool_call' || step.type === 'tool_result') {
      phases[1].steps.push(step);
    } else if (step.type === 'executor_content') {
      phases[2].steps.push(step);
    }
  });

  // Determine phase status
  phases.forEach((phase) => {
    if (phase.steps.length === 0) {
      phase.status = 'pending';
    } else if (phase.steps.some((s) => s.status === 'error')) {
      phase.status = 'error';
    } else if (phase.steps.some((s) => s.status === 'in_progress')) {
      phase.status = 'in_progress';
    } else if (phase.steps.every((s) => s.status === 'complete')) {
      phase.status = 'complete';
    } else {
      phase.status = 'in_progress';
    }
  });

  return phases;
}

const statusIcon = (status: ExecutionStep['status']) => {
  switch (status) {
    case 'in_progress':
      return <CircularProgress size={16} />;
    case 'complete':
      return <Icon sx={{ fontSize: 16, color: 'success.main' }}>check_circle</Icon>;
    case 'error':
      return <Icon sx={{ fontSize: 16, color: 'error.main' }}>error</Icon>;
    default:
      return <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>radio_button_unchecked</Icon>;
  }
};

const phaseStatusChip = (status: Phase['status']) => {
  switch (status) {
    case 'in_progress':
      return <Chip size="small" label="Running" color="primary" />;
    case 'complete':
      return <Chip size="small" label="Complete" color="success" />;
    case 'error':
      return <Chip size="small" label="Error" color="error" />;
    default:
      return <Chip size="small" label="Pending" color="default" />;
  }
};

const StepRow: React.FC<{
  step: ExecutionStep;
  onClick?: () => void;
}> = ({ step, onClick }) => {
  const stepLabel = (() => {
    if (step.type === 'planner_decision') {
      const rawDecision = step.data?.decision;
      const decisionAction =
        typeof rawDecision === 'string'
          ? rawDecision
          : (rawDecision?.action ?? rawDecision?.decision ?? 'deciding');
      if (decisionAction === 'start') return 'Planning started';
      if (decisionAction === 'complete') return 'Planning complete';
      const iter = step.iteration ?? '-';
      const toolName = step.data?.tool_name ?? rawDecision?.tool?.name;
      return `Iteration ${iter}: ${decisionAction}${toolName ? ` [${toolName}]` : ''}`;
    }
    if (step.type === 'tool_call') return `Calling tool: ${step.data?.name ?? 'unknown'}`;
    if (step.type === 'tool_result') {
      const toolName = step.data?.tool ?? step.data?.name ?? 'unknown';
      if (step.status === 'error') return `${toolName} failed`;
      return `${toolName} completed`;
    }
    if (step.type === 'executor_content') return 'Generating response';
    return 'Unknown step';
  })();

  // Format duration for display - only show for completed steps
  const durationText = (() => {
    if (step.status !== 'complete' || typeof step.duration !== 'number' || step.duration < 0) {
      return null;
    }
    if (step.duration >= 1000) {
      return `${(step.duration / 1000).toFixed(1)}s`;
    }
    return `${Math.round(step.duration)}ms`;
  })();

  const reflectionText =
    step.type === 'planner_decision' && typeof step.data?.reflection_text === 'string'
      ? step.data.reflection_text
      : null;

  const progressText = (() => {
    if (step.type !== 'planner_decision') return null;
    const progress = step.data?.progress;
    if (typeof progress === 'string' && progress.trim().length > 0) {
      return progress;
    }
    if (progress && typeof progress === 'object') {
      const pieces: string[] = [];
      if (typeof progress.percent_complete === 'number') {
        pieces.push(`${Math.round(progress.percent_complete)}% complete`);
      }
      if (progress.current_step) {
        pieces.push(`Current step ${progress.current_step}`);
      }
      if (Array.isArray(progress.completed_steps) && progress.completed_steps.length > 0) {
        pieces.push(`Finished ${progress.completed_steps.length} steps`);
      }
      return pieces.length > 0 ? pieces.join(' • ') : null;
    }
    return null;
  })();

  const strategyText = (() => {
    if (step.type !== 'planner_decision') return null;
    const strategy = step.data?.strategy;
    if (typeof strategy === 'string') {
      return strategy;
    }
    if (strategy && typeof strategy === 'object') {
      if (typeof strategy.approach === 'string') return strategy.approach;
      if (typeof strategy.goal_understanding === 'string') return strategy.goal_understanding;
    }
    return null;
  })();

  return (
    <div
      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className="flex-shrink-0">{statusIcon(step.status)}</div>
      <div className="flex-1 min-w-0">
        <Typography variant="body2" className="font-medium truncate">
          {stepLabel}
        </Typography>
        {reflectionText && (
          <Typography variant="caption" color="text.secondary" className="block truncate">
            🪞 {reflectionText.length > 120 ? `${reflectionText.slice(0, 120)}…` : reflectionText}
          </Typography>
        )}
        {progressText && (
          <Typography variant="caption" color="text.secondary" className="block truncate">
            📈 {progressText.length > 140 ? `${progressText.slice(0, 140)}…` : progressText}
          </Typography>
        )}
        {strategyText && (
          <Typography variant="caption" color="text.secondary" className="block truncate">
            🧭 {strategyText.length > 140 ? `${strategyText.slice(0, 140)}…` : strategyText}
          </Typography>
        )}
      </div>
      {durationText && (
        <Typography variant="caption" color="text.secondary" className="flex-shrink-0">
          {durationText}
        </Typography>
      )}
    </div>
  );
};

export default function PhaseAccordion({
  steps,
  isStreaming,
  error,
  onStepClick,
}: PhaseAccordionProps) {
  const phases = groupStepsIntoPhases(steps);
  const [expanded, setExpanded] = React.useState<string[]>([
    'Planning',
    'Tool Execution',
    'Response Generation',
  ]);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = React.useRef<number | undefined>(undefined);

  // Auto-expand phases that have new steps while streaming
  React.useEffect(() => {
    if (!isStreaming || phases.length === 0) {
      return;
    }
    setExpanded((prev) => {
      const next = new Set(prev);
      phases.forEach((phase) => {
        if (phase.steps.length > 0) {
          next.add(phase.name);
        }
      });
      return prev.length === next.size ? prev : Array.from(next);
    });
  }, [isStreaming, phases]);

  // Auto-scroll to bottom when new steps arrive
  React.useEffect(() => {
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    if (isStreaming && containerRef.current && steps.length > 0) {
      scrollTimeoutRef.current = window.setTimeout(() => {
        containerRef.current?.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [steps.length, isStreaming]);

  React.useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleAccordionChange = (phaseName: string) => {
    setExpanded((prev) =>
      prev.includes(phaseName) ? prev.filter((p) => p !== phaseName) : [...prev, phaseName]
    );
  };

  if (steps.length === 0 && !isStreaming && !error) {
    return (
      <div className="text-center p-8 text-gray-500">
        <Icon sx={{ fontSize: 48, opacity: 0.5 }}>schedule</Icon>
        <Typography variant="body2" className="mt-2">
          Waiting for execution to start...
        </Typography>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="space-y-2 max-h-[60vh] overflow-y-auto pr-1"
      style={{ scrollBehavior: 'smooth' }}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
          <Typography variant="body2" color="error">
            <Icon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }}>error</Icon>
            {error}
          </Typography>
        </div>
      )}

      {phases.map((phase) => (
        <Accordion
          key={phase.name}
          expanded={expanded.includes(phase.name)}
          onChange={() => handleAccordionChange(phase.name)}
          disabled={phase.steps.length === 0}
        >
          <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
            <div className="flex items-center justify-between w-full pr-2">
              <div className="flex items-center gap-2">
                <Icon
                  sx={{ color: phase.status === 'complete' ? 'success.main' : 'text.secondary' }}
                >
                  {phase.icon}
                </Icon>
                <Typography variant="subtitle2">{phase.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ({phase.steps.length} {phase.steps.length === 1 ? 'step' : 'steps'})
                </Typography>
              </div>
              {phaseStatusChip(phase.status)}
            </div>
          </AccordionSummary>
          <AccordionDetails sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            <div className="space-y-1">
              {phase.steps.length === 0 ? (
                <Typography variant="caption" color="text.secondary" className="italic">
                  No steps in this phase yet
                </Typography>
              ) : (
                phase.steps.map((step) => (
                  <StepRow key={step.id} step={step} onClick={() => onStepClick?.(step)} />
                ))
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}
